// ── Step 01 — Resident Information ───────────────────────────────────────────
// All checkbox/select option lists for Step01ResidentInfo form.
// Kept here so they can be reused in validation, review summaries, PDF
// generation, or other steps without importing the whole form component.

export const STEP01_GENDER_OPTIONS = [
    ["isResidentGenderMale", "Male"],
    ["isResidentGenderFemale", "Female"],
] as const;

export const STEP01_MARITAL_STATUS_OPTIONS = [
    ["isResidentMarried", "Married"],
    ["isResidentDivorced", "Divorced"],
    ["isResidentWidowed", "Widowed"],
] as const;

export const STEP01_INSURANCE_OPTIONS = [
    { field: "hasMedicaidInsurance" as const, label: "Medicaid" },
    { field: "hasMedicareInsurance" as const, label: "Medicare" },
    { field: "hasHospiceInsurance" as const, label: "Hospice" },
    { field: "isVeteran" as const, label: "Veteran" },
];

export const STEP01_ADVANCE_DIRECTIVE_OPTIONS = [
    ["hasAdvanceDirectivesOnFile", "Advance Directives"],
    ["hasPOLSTOnFile", "POLST"],
    ["hasFuneralArrangementsOnFile", "Funeral Arrangements"],
] as const;

export const STEP01_SPECIALIST_FIELDS = [
    ["specialist1Name", "specialist1Phone", "Specialist 1"],
    ["specialist2Name", "specialist2Phone", "Specialist 2"],
] as const;

export const STEP01_DENTIST_PHARMACY_FIELDS = [
    ["dentistName", "dentistPhone", "Dentist"],
    ["pharmacyName", "pharmacyPhone", "Pharmacy"],
] as const;

// ── Step 02 — Emergency Evacuation ────────────────────────────────────────────

/** Simple (no paired text field) checkbox options in the Special Instructions panel. */
export const STEP02_SPECIAL_INSTRUCTION_OPTIONS = [
    ["requiresTotalOneToOneAssistance", "Requires total 1:1 assistance"],
    ["requiresWheelchair", "Wheelchair"],
    ["bedroomMustBeOnGroundLevel", "Must have bedroom located on ground level"],
] as const;

// ── Step 03 — Dementia Screening ──────────────────────────────────────────────

/** Category 2: behavioral indicators (at least one must exist within the last 30 days) */
export const STEP03_BEHAVIORAL_INDICATORS = [
    ["hasDecisionMakingDifficulty", "Ability to make decisions about daily life is poor; requires reminders, cues and supervision"],
    ["hasHistoryOfPhysicalInjuryToOthers", "History of physical injury to staff/others"],
    ["isCombative", "Combative"],
    ["isResistiveToCare", "Resistive to care"],
    ["hasSexualActingOut", "Sexual acting out"],
    ["hasSexualAggression", "Sexual aggression"],
    ["isAgitatedOrWandersAtNight", "Agitated or wanders at night"],
    ["eatsNonEdibleObjects", "Eats non-edible objects"],
    ["hasInappropriateScreaming", "Inappropriate screaming, yelling or verbal noises"],
    ["hasGottenLostOutside", "Has left home and gotten lost when trying to return"],
    ["hasRepetitiveMovement", "Repetitive movement / pacing, hand-wringing, fidgeting"],
    ["leavesStoveOn", "Leaves stove on after cooking"],
    ["isAggressiveOrIntimidating", "Aggressive / intimidating"],
    ["hasExitSeekingBehaviors", "Exit seeking behaviors"],
    ["isEasilyIrritated", "Easily irritated / upset / agitated"],
    ["seeksConstantAttention", "Seeks / demands constant attention / reassurance"],
] as const;

// ── Step 04 — Current Medical Diagnosis ───────────────────────────────────────
// (No option constants needed — form uses a dynamic list of DiagnosisEntry items)

/** Stepper step definitions — single source of truth used by both the Stepper and the progress bar */
export const NURSE_FORM_STEPS = [
    { label: "Resident Info", icon: "pi pi-user" },
    { label: "Evacuation", icon: "pi pi-exclamation-triangle" },
    { label: "Dementia Screening", icon: "pi pi-brain" },
    { label: "Medical Diagnoses", icon: "pi pi-heart" },
    { label: "Medications", icon: "pi pi-file-edit" },
    { label: "Psychosocial", icon: "pi pi-users" },
    { label: "Mental Health", icon: "pi pi-heart-fill" },
    { label: "Cognition", icon: "pi pi-lightbulb" },
    { label: "Sensory", icon: "pi pi-eye" },
    { label: "Medical Review", icon: "pi pi-list-check" },
    { label: "Care Plan Page", icon: "pi pi-clipboard" },
    { label: "Body Chart", icon: "pi pi-map" },
    { label: "Daily Living", icon: "pi pi-home" },
    { label: "Behavior", icon: "pi pi-flag" },
    { label: "Signatures", icon: "pi pi-check-circle" },
] as const;

export const NURSE_FORM_ASSESSMENT_STEPS = [
    NURSE_FORM_STEPS[0],
    NURSE_FORM_STEPS[1],
    NURSE_FORM_STEPS[2],
    NURSE_FORM_STEPS[3],
    NURSE_FORM_STEPS[4],
    NURSE_FORM_STEPS[5],
    NURSE_FORM_STEPS[6],
    NURSE_FORM_STEPS[7],
    NURSE_FORM_STEPS[8],
    NURSE_FORM_STEPS[9],
] as const;

export const NURSE_FORM_CARE_PLAN_STEPS = [
    NURSE_FORM_STEPS[10],
    NURSE_FORM_STEPS[11],
    NURSE_FORM_STEPS[12],
    NURSE_FORM_STEPS[13],
    NURSE_FORM_STEPS[14],
] as const;
